import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStoreCart } from '../context/StoreCartContext';
import { CartQuantityControls, GoldenAmount } from '../components/store/StoreUi';
import api from '../utils/api';

export default function StoreCart() {
  const { user } = useAuth();
  const { items, itemCount, totalGolden, updateQuantity, removeItem } = useStoreCart();
  const [balance, setBalance] = useState(user?.goldenTickets ?? 0);

  useEffect(() => {
    if (!user) return;
    api
      .get('/tickets/balances')
      .then(({ data }) => setBalance(data?.goldenTickets ?? user.goldenTickets ?? 0))
      .catch(() => setBalance(user.goldenTickets ?? 0));
  }, [user]);

  const shortfall = Math.max(0, totalGolden - balance);

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-8 sm:py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Link to="/store" className="text-sm font-semibold text-amber-700 dark:text-amber-400 hover:underline">← Continue shopping</Link>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black text-slate-950 dark:text-white">Your cart</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{itemCount} item{itemCount === 1 ? '' : 's'}</p>
          </div>
          {user && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-950/20 px-4 py-2">
              <span className="mr-2 text-xs font-bold uppercase text-slate-500">Balance</span>
              <GoldenAmount amount={balance} />
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-white/15 bg-white dark:bg-slate-900 px-6 py-16 text-center">
            <p className="text-6xl" aria-hidden>🛒</p>
            <h2 className="mt-4 text-2xl font-black text-slate-900 dark:text-white">Your cart is empty</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Spend your golden tickets on exclusive rewards.</p>
            <Link to="/store" className="mt-6 inline-flex rounded-xl bg-amber-400 px-6 py-3 font-black text-slate-950">
              Explore the store
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
            <section className="space-y-3">
              {items.map((item) => (
                <article key={item.key} className="flex gap-4 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-4">
                  <Link to={`/store/product/${item.slug}`} className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-3xl" aria-hidden>🎁</div>
                    )}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/store/product/${item.slug}`} className="font-bold text-slate-900 dark:text-white hover:text-amber-700">
                          {item.name}
                        </Link>
                        {item.variantName && <p className="mt-1 text-xs text-slate-500">{item.variantName}</p>}
                        <div className="mt-2"><GoldenAmount amount={item.priceGolden} size="sm" /></div>
                      </div>
                      <button onClick={() => removeItem(item.key)} className="text-sm font-semibold text-red-600 dark:text-red-400 hover:underline">
                        Remove
                      </button>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <CartQuantityControls
                        quantity={item.quantity}
                        onDecrease={() => updateQuantity(item.key, item.quantity - 1)}
                        onIncrease={() => updateQuantity(item.key, item.quantity + 1)}
                        disableIncrease={item.quantity >= Math.min(25, item.maxStock > 0 ? item.maxStock : 25)}
                        compact
                      />
                      <GoldenAmount amount={item.priceGolden * item.quantity} />
                    </div>
                  </div>
                </article>
              ))}
            </section>

            <aside className="h-fit rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5 sm:p-6 lg:sticky lg:top-32">
              <h2 className="text-lg font-black text-slate-900 dark:text-white">Order summary</h2>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between text-slate-500 dark:text-slate-400">
                  <span>Items ({itemCount})</span>
                  <GoldenAmount amount={totalGolden} size="sm" />
                </div>
                <div className="flex justify-between text-slate-500 dark:text-slate-400">
                  <span>Shipping</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">Included</span>
                </div>
                <div className="border-t border-slate-200 dark:border-white/10 pt-4 flex items-end justify-between">
                  <span className="font-bold text-slate-900 dark:text-white">Total</span>
                  <GoldenAmount amount={totalGolden} size="lg" />
                </div>
              </div>

              {user && shortfall > 0 && (
                <div className="mt-5 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-3">
                  <p className="text-sm font-bold text-red-800 dark:text-red-300">Not enough golden tickets</p>
                  <p className="mt-1 text-xs text-red-700 dark:text-red-400">
                    You have {balance.toLocaleString()} and need {shortfall.toLocaleString()} more.
                  </p>
                </div>
              )}

              {!user ? (
                <Link to="/login" state={{ from: '/store/cart' }} className="mt-5 flex w-full justify-center rounded-xl bg-slate-950 dark:bg-white px-5 py-3.5 font-black text-white dark:text-slate-950">
                  Log in to checkout
                </Link>
              ) : (
                <Link
                  to={shortfall > 0 ? '#' : '/store/checkout'}
                  onClick={(event) => shortfall > 0 && event.preventDefault()}
                  aria-disabled={shortfall > 0}
                  className={`mt-5 flex w-full justify-center rounded-xl px-5 py-3.5 font-black ${
                    shortfall > 0
                      ? 'cursor-not-allowed bg-slate-200 dark:bg-slate-700 text-slate-500'
                      : 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950 shadow-lg shadow-amber-500/20'
                  }`}
                >
                  {shortfall > 0 ? `Need ${shortfall} more golden` : 'Proceed to checkout'}
                </Link>
              )}
              <p className="mt-3 text-center text-xs leading-relaxed text-slate-400">
                Prices and stock are confirmed securely during checkout.
              </p>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
